import {
  Component,
  ChangeDetectionStrategy,
  signal,
  computed,
  inject,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ModalComponent, ModalConfig } from '../modal/modal';
import { IconComponent } from '../icon/icon';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { OrganizationType, OrgCreateRequest } from '../../models';
import { StateStoreService } from '../../services/state-store/state-store';
import { injectActiveTranslation } from '../../services/locale/active-translation';
import { getUserFriendlyErrorMessage } from '../../utils/error.utils';
import { isValidEmail as validateEmail } from '../../utils/validation.utils';

@Component({
  selector: 'app-create-customer-org-modal',
  imports: [CommonModule, FormsModule, TranslocoModule, ModalComponent, IconComponent],
  templateUrl: './create-customer-org-modal.html',
  styleUrl: './create-customer-org-modal.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateCustomerOrgModal {
  private readonly orgManagementApi = inject(OrgManagementApi);
  private readonly stateService = inject(StateStoreService);
  private readonly transloco = inject(TranslocoService);
  private readonly activeTranslation = injectActiveTranslation();

  // Output events
  public readonly organizationCreated = output<string>();
  public readonly modalClosed = output<void>();

  // Form state
  private readonly _isVisible = signal(false);
  private readonly _isLoading = signal(false);
  private _error = signal<string | null>(null);

  // Form data
  private readonly _organizationName = signal('');
  private readonly _adminEmail = signal('');

  // Public accessors for form data
  get organizationName(): string {
    return this._organizationName();
  }
  set organizationName(value: string) {
    this._organizationName.set(value);
  }

  get adminEmail(): string {
    return this._adminEmail();
  }
  set adminEmail(value: string) {
    this._adminEmail.set(value);
  }

  // Public readonly state
  public readonly isVisible = this._isVisible.asReadonly();
  public readonly isLoading = this._isLoading.asReadonly();
  public readonly error = this._error.asReadonly();

  // Modal configuration
  public readonly modalConfig = computed<ModalConfig>(() => {
    this.activeTranslation();
    return {
      title: this.t('title'),
      size: 'medium',
      showCloseButton: true,
      showFooter: true,
      primaryButtonText: this.t('submit'),
      secondaryButtonText: this.transloco.translate('common.cancel'),
    };
  });

  // Computed properties
  public readonly isFormValid = computed(() => {
    return (
      this._organizationName().trim().length > 0 &&
      this._adminEmail().trim().length > 0 &&
      this.isValidEmail(this._adminEmail().trim())
    );
  });

  public readonly primaryButtonDisabled = computed(() => {
    return this._isLoading() || !this.isFormValid();
  });

  public readonly primaryButtonLoading = computed(() => {
    return this._isLoading();
  });

  /**
   * Show the modal
   */
  public show(): void {
    this._isVisible.set(true);
    this.resetForm();
  }

  /**
   * Hide the modal
   */
  public hide(): void {
    this._isVisible.set(false);
    this.resetForm();
  }

  /**
   * Handle modal close
   */
  public onModalClosed(): void {
    this.hide();
    this.modalClosed.emit();
  }

  /**
   * Handle modal cancel
   */
  public onModalCancelled(): void {
    this.hide();
    this.modalClosed.emit();
  }

  /**
   * Handle form submission
   */
  public onModalConfirmed(): void {
    if (!this.isFormValid()) {
      this._error.set(this.t('error_invalid'));
      return;
    }

    this.createOrganization();
  }

  /**
   * Create the organization
   */
  private createOrganization(): void {
    this._isLoading.set(true);
    this._error.set(null);

    const request: OrgCreateRequest = {
      managedBy: this.stateService.selectedOrgId() ?? '',
      orgName: this.organizationName.trim(),
      adminEmail: this.adminEmail.trim(),
      orgType: OrganizationType.STANDARD,
    };

    console.debug('🏢 CreateCustomerOrgModal: Creating organization', request);

    this.orgManagementApi.createOrg(request).subscribe({
      next: response => {
        console.debug('✅ CreateCustomerOrgModal: Organization created successfully', response);
        this._isLoading.set(false);
        this.organizationCreated.emit(response.principal_id);
        this.hide();
      },
      error: error => {
        console.error('❌ CreateCustomerOrgModal: Failed to create organization', error);
        this._isLoading.set(false);
        this._error.set(getUserFriendlyErrorMessage(error, this.t('error_failed'), this.transloco));
      },
    });
  }

  /**
   * Reset form to initial state
   */
  private resetForm(): void {
    this._organizationName.set('');
    this._adminEmail.set('');
    this._error.set(null);
    this._isLoading.set(false);
  }

  /**
   * Validate email format - uses utility function
   */
  private isValidEmail = validateEmail;

  /**
   * Clear error message
   */
  public clearError(): void {
    this._error.set(null);
  }

  private t(key: string): string {
    return this.transloco.translate(`modals.create_customer_org.${key}`);
  }
}
